import React, { useState, useRef } from 'react';
import { Box } from '@mui/material';

const PinchZoomPanContainer = ({ children }) => {
    const [transform, setTransform] = useState({ scale: 1, x: 0, y: 0 });
    const [isPanning, setIsPanning] = useState(false);
    const containerRef = useRef(null);
    const contentRef = useRef(null);

    const touchState = useRef({
        startDistance: 0,
        startScale: 1,
        startX: 0,
        startY: 0,
        startPanX: 0,
        startPanY: 0,
        lastScale: 1,
        // Pinch focal point (midpoint of two fingers) relative to container centre
        focalX: 0,
        focalY: 0,
    });

    const getBounds = (scale) => {
        if (!containerRef.current || !contentRef.current) return { maxX: 0, maxY: 0 };
        const container = containerRef.current.getBoundingClientRect();
        // Use offsetWidth/Height to get base dimensions before transform
        const baseWidth = contentRef.current.offsetWidth;
        const baseHeight = contentRef.current.offsetHeight;

        const scaledWidth = baseWidth * scale;
        const scaledHeight = baseHeight * scale;

        const maxX = Math.max(0, (scaledWidth - container.width) / 2);
        const maxY = Math.max(0, (scaledHeight - container.height) / 2);

        return { maxX, maxY };
    };

    const applyBounds = (x, y, scale) => {
        const { maxX, maxY } = getBounds(scale);
        return {
            x: Math.min(Math.max(x, -maxX), maxX),
            y: Math.min(Math.max(y, -maxY), maxY)
        };
    };

    const onTouchStart = (e) => {
        if (e.touches.length === 2) {
            const touch1 = e.touches[0];
            const touch2 = e.touches[1];
            touchState.current.startDistance = Math.hypot(
                touch2.clientX - touch1.clientX,
                touch2.clientY - touch1.clientY
            );
            touchState.current.startScale = transform.scale;
            touchState.current.startPanX = transform.x;
            touchState.current.startPanY = transform.y;

            // Compute pinch midpoint relative to container centre
            if (containerRef.current) {
                const rect = containerRef.current.getBoundingClientRect();
                const midClientX = (touch1.clientX + touch2.clientX) / 2;
                const midClientY = (touch1.clientY + touch2.clientY) / 2;
                // Position relative to container centre
                touchState.current.focalX = midClientX - (rect.left + rect.width / 2);
                touchState.current.focalY = midClientY - (rect.top + rect.height / 2);
            }

            setIsPanning(false);
        } else if (e.touches.length === 1 && transform.scale > 1) {
            setIsPanning(true);
            touchState.current.startX = e.touches[0].clientX;
            touchState.current.startY = e.touches[0].clientY;
            touchState.current.startPanX = transform.x;
            touchState.current.startPanY = transform.y;
        }
    };

    const onTouchMove = (e) => {
        if (e.touches.length === 2) {
            const touch1 = e.touches[0];
            const touch2 = e.touches[1];

            const currentDistance = Math.hypot(
                touch2.clientX - touch1.clientX,
                touch2.clientY - touch1.clientY
            );

            if (touchState.current.startDistance > 0) {
                // ✅ Absolute scale — computed from gesture-start, not from prev frame.
                const ratio = currentDistance / touchState.current.startDistance;
                let newScale = touchState.current.startScale * ratio;
                newScale = Math.min(Math.max(1, newScale), 5);

                if (newScale === 1) {
                    setTransform({ scale: 1, x: 0, y: 0 });
                } else {
                    // Use the focal point & pan that were saved ONCE at touchStart.
                    // This avoids the incremental-ratio bug where scaleRatio ≈ 1
                    // every frame because prev.scale already equals newScale.
                    const {
                        focalX,
                        focalY,
                        startScale,
                        startPanX,
                        startPanY,
                    } = touchState.current;

                    const scaleRatio = newScale / startScale;

                    // Keep the pinch midpoint fixed on screen:
                    //   newPan = focal − (focal − startPan) × (newScale / startScale)
                    const newX = focalX - (focalX - startPanX) * scaleRatio;
                    const newY = focalY - (focalY - startPanY) * scaleRatio;

                    const bounded = applyBounds(newX, newY, newScale);
                    setTransform({ scale: newScale, x: bounded.x, y: bounded.y });
                }
            }
        } else if (e.touches.length === 1 && transform.scale > 1 && isPanning) {
            const dx = e.touches[0].clientX - touchState.current.startX;
            const dy = e.touches[0].clientY - touchState.current.startY;

            const newX = touchState.current.startPanX + dx;
            const newY = touchState.current.startPanY + dy;

            const bounded = applyBounds(newX, newY, transform.scale);

            setTransform(prev => ({ ...prev, x: bounded.x, y: bounded.y }));
        }
    };

    const onTouchEnd = (e) => {
        if (e.touches.length < 2) {
            touchState.current.startDistance = 0;
            if (e.touches.length === 1) {
                // Reset pan start point for the remaining finger
                touchState.current.startX = e.touches[0].clientX;
                touchState.current.startY = e.touches[0].clientY;
                touchState.current.startPanX = transform.x;
                touchState.current.startPanY = transform.y;
                setIsPanning(true);
            }
        }
        if (e.touches.length === 0) {
            setIsPanning(false);
        }
    };

    const onDoubleClick = (e) => {
        if (transform.scale > 1) {
            setTransform({ scale: 1, x: 0, y: 0 });
        } else {
            const rect = containerRef.current.getBoundingClientRect();
            const focalX = e.clientX - (rect.left + rect.width / 2);
            const focalY = e.clientY - (rect.top + rect.height / 2);

            const newScale = 2.5;
            // Focal point zoom math: newPos = focal - (focal - oldPos) * ratio
            const newX = -focalX * (newScale - 1);
            const newY = -focalY * (newScale - 1);

            const bounded = applyBounds(newX, newY, newScale);
            setTransform({ scale: newScale, x: bounded.x, y: bounded.y });
        }
    };

    const onMouseDown = (e) => {
        if (transform.scale > 1) {
            setIsPanning(true);
            touchState.current.startX = e.clientX;
            touchState.current.startY = e.clientY;
            touchState.current.startPanX = transform.x;
            touchState.current.startPanY = transform.y;
        }
    };

    const onMouseMove = (e) => {
        if (transform.scale > 1 && isPanning) {
            const dx = e.clientX - touchState.current.startX;
            const dy = e.clientY - touchState.current.startY;
            const newX = touchState.current.startPanX + dx;
            const newY = touchState.current.startPanY + dy;
            const bounded = applyBounds(newX, newY, transform.scale);
            setTransform(prev => ({ ...prev, x: bounded.x, y: bounded.y }));
        }
    };

    const onMouseUp = () => {
        setIsPanning(false);
    };

    return (
        <Box
            ref={containerRef}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            onDoubleClick={onDoubleClick}
            sx={{
                width: '100%',
                height: '100%',
                minHeight: '100vh',
                minWidth: '100vw',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                backgroundColor: 'transparent',
                touchAction: 'none',
                cursor: transform.scale > 1 ? (isPanning ? 'grabbing' : 'grab') : 'zoom-in'
            }}
        >
            <Box
                ref={contentRef}
                sx={{
                    transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
                    transition: (touchState.current.startDistance === 0 && !isPanning) ? 'transform 0.3s cubic-bezier(0.2, 0, 0, 1)' : 'none',
                    transformOrigin: 'center',
                    willChange: 'transform',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}
            >
                {children}
            </Box>
        </Box>
    );
};

export default PinchZoomPanContainer;
